import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './Leaderboard.module.css';

/**
 * Leaderboard Component
 * Displays animated latest wins table with shifting rows
 * Based on old HelloGamesTable component
 */

const games = [
  { name: "Blackjack", icon: "🃏", className: "blackjack", imagePath: "/assets/live/1.webp" },
  { name: "Roulette", icon: "🎰", className: "roulette", imagePath: "/assets/live/2.webp" },
  { name: "Baccarat", icon: "🎴", className: "baccarat", imagePath: "/assets/live/3.webp" },
  { name: "Poker", icon: "♠️", className: "poker", imagePath: "/assets/live/4.webp" },
  { name: "Live Casino", icon: "🎲", className: "liveCasino", imagePath: "/assets/live/5.webp" },
  { name: "Andante", icon: "🎯", className: "andante", imagePath: "/assets/live/7.webp" },
  { name: "Lightning", icon: "⚡", className: "lightning", imagePath: "/assets/live/8.webp" },
];

const currencies = [
  { name: "BTC", className: "btc", imagePath: "/assets/images/coins/BTC.webp" },
  { name: "ETH", className: "eth", imagePath: "/assets/images/coins/ETH.webp" },
  { name: "LTC", className: "ltc", imagePath: "/assets/images/coins/LTC.webp" },
  { name: "DOGE", className: "doge", imagePath: "/assets/images/coins/DOGE.webp" },
  { name: "BCH", className: "bch", imagePath: "/assets/images/coins/BCH.webp" },
  { name: "USDT", className: "usdt", imagePath: "/assets/images/coins/USDT.webp" },
];

// Name parts for username generation
const nameParts = [
  "Crypto", "Bitcoin", "Satoshi", "Hodl", "Moon", "Rocket", "Diamond", "Whale",
  "Bull", "Bear", "Degen", "Ape", "Chain", "Block", "Hash", "Simon", "Alex",
  "Max", "Elon", "Vital", "Sam", "Mike", "John", "Lisa", "Emma", "Sophia",
  "Jack", "Ryan", "Gia", "Leo", "Zack",
];

const nameSuffixes = [
  "TheMoon", "Hodler", "Trader", "Winner", "Pro", "Master", "King", "Queen",
  "Guru", "Wizard", "Ninja", "Whale", "Shark", "Hunter", "Boss",
];

// Track used usernames to ensure uniqueness
const usedUsernames = new Set();

// Generate a unique username
function generateUsername() {
  let username = "";
  let attempts = 0;

  do {
    const pattern = Math.floor(Math.random() * 5);
    switch (pattern) {
      case 0:
        username = `${nameParts[Math.floor(Math.random() * nameParts.length)]}${Math.floor(Math.random() * 10000)}${nameSuffixes[Math.floor(Math.random() * nameSuffixes.length)]}`;
        break;
      case 1:
        username = `${nameParts[Math.floor(Math.random() * nameParts.length)]}${Math.floor(Math.random() * 100000)}`;
        break;
      case 2:
        username = `${nameParts[Math.floor(Math.random() * nameParts.length)]}${nameParts[Math.floor(Math.random() * nameParts.length)]}${Math.floor(Math.random() * 1000)}`;
        break;
      case 3:
        username = `${nameParts[Math.floor(Math.random() * nameParts.length)]}${Math.floor(Math.random() * 1000)}${nameParts[Math.floor(Math.random() * nameParts.length)]}${Math.floor(Math.random() * 100)}`;
        break;
      case 4:
        username = `${nameParts[Math.floor(Math.random() * nameParts.length)]}${Math.random() > 0.5 ? "_" : ""}${nameParts[Math.floor(Math.random() * nameParts.length)]}${Math.floor(Math.random() * 1000)}`;
        break;
    }
    // Remove any spaces and ensure no whitespace
    username = username.trim().replace(/\s+/g, '');
    attempts++;
    if (attempts > 100) {
      username = `User${Math.random().toString(20).substring(2, 10)}`;
      break;
    }
  } while (usedUsernames.has(username));

  usedUsernames.add(username);
  return username;
}

function generateRandomBet() {
  const game = games[Math.floor(Math.random() * games.length)];
  const currency = currencies[Math.floor(Math.random() * currencies.length)];
  const payout = (Math.random() * 10).toFixed(2);
  const profit = (Math.random() * 20).toFixed(2);
  const betId = Math.floor(Math.random() * 900000000) + 100000000;

  return {
    id: Math.random().toString(36).substr(2, 9),
    game,
    player: generateUsername(),
    betId: betId.toString(),
    payout: `${payout}x`,
    profit: {
      amount: profit,
      currency: currency,
    },
    isNew: true,
  };
}

// Generate initial bets
const generateInitialBets = () => {
  return Array.from({ length: 10 }, (_, i) => {
    const game = games[Math.floor(Math.random() * games.length)];
    const currency = currencies[Math.floor(Math.random() * currencies.length)];
    const payout = (Math.random() * 10).toFixed(2);
    const profit = (Math.random() * 0.1).toFixed(2);
    const betId = Math.floor(Math.random() * 900000000) + 100000000;

    return {
      id: `initial-${i}`,
      game,
      player: generateUsername(),
      betId: betId.toString(),
      payout: `${payout}x`,
      profit: {
        amount: profit,
        currency,
      },
      isNew: false,
    };
  });
};

const Leaderboard = () => {
  const [bets, setBets] = useState(generateInitialBets());

  useEffect(() => {
    const interval = setInterval(() => {
      const newBet = generateRandomBet();
      setBets((prevBets) => {
        // Always maintain exactly 10 rows
        const updatedBets = [newBet, ...prevBets.slice(0, 9)];
        // Only mark the new bet as new
        return updatedBets.map((bet, index) => ({
          ...bet,
          isNew: index === 0,
        }));
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Latest Wins</h2>
      </div>

      <div className={styles.tableWrapper}>
        {/* Header */}
        <div className={styles.tableHeader}>
          <div className={styles.headerCell}>Game</div>
          <div className={styles.headerCell}>Player</div>
          <div className={styles.headerCell}>Bet ID</div>
          <div className={styles.headerCell}>Payout</div>
          <div className={styles.headerCell}>Profit</div>
        </div>

        {/* Bet Rows - Always exactly 10 rows */}
        <div className={styles.tableBody}>
          <AnimatePresence mode="popLayout">
            {bets.map((bet, index) => (
              <motion.div
                key={bet.id}
                layout
                initial={bet.isNew ? { opacity: 0, y: -20, scale: 0.95 } : false}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                transition={{
                  layout: { duration: 0.3, ease: "easeOut" },
                  opacity: { duration: 0.2 },
                  scale: { duration: 0.2 }
                }}
                className={`${styles.betRow} ${bet.isNew ? styles.new : ""}`}
              >
              {/* Game */}
              <div className={styles.gameCell}>
                <img
                  src={bet.game.imagePath}
                  alt={bet.game.name}
                  className={styles.gameIcon}
                  onError={(e) => {
                    e.target.src = '/assets/images/default_card3.png';
                  }}
                />
                <span className={styles.gameName}>{bet.game.name}</span>
              </div>

              {/* Player */}
              <div className={styles.playerCell}>
                <span className={styles.playerName}>{bet.player}</span>
              </div>

              {/* Bet ID */}
              <div className={styles.betIdCell}>
                <span className={styles.betId}>{bet.betId}</span>
              </div>

              {/* Payout */}
              <div className={styles.payoutCell}>
                <span className={styles.payout}>{bet.payout}</span>
              </div>

              {/* Profit */}
              <div className={styles.profitCell}>
                <img
                  src={bet.profit.currency.imagePath}
                  alt={bet.profit.currency.name}
                  className={styles.currencyIcon}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <motion.span 
                  className={styles.profitAmount}
                  initial={bet.isNew ? { scale: 0 } : false}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                >
                  {bet.profit.currency.name} {bet.profit.amount}
                </motion.span>
              </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
